import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import {
  Container,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material'
import { ArrowBack, PictureAsPdf, Download } from '@mui/icons-material'
import mermaid from 'mermaid'

mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'Arial, sans-serif'
})

const DiagramaRender = ({ mermaidCode }) => {
  const [svg, setSvg] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!mermaidCode || !mermaidCode.trim()) return

    const render = async () => {
      setLoading(true)
      setError('')
      try {
        const id = `public-mermaid-${Date.now()}`
        const { svg: rendered } = await mermaid.render(id, mermaidCode)
        if (!cancelled) setSvg(rendered)
      } catch (err) {
        console.error('Error renderizando diagrama:', err)
        if (!cancelled) setError('No se pudo renderizar el diagrama')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    render()

    return () => {
      cancelled = true
    }
  }, [mermaidCode])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }
  if (error) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
        {error}
      </Typography>
    )
  }
  if (!svg) return null

  return (
    <Box
      sx={{
        textAlign: 'center',
        '& svg': { maxWidth: '100%', height: 'auto' }
      }}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}

const formatDefiniciones = (definiciones) => {
  if (!definiciones) return null
  const parts = definiciones.split(';').map((p) => p.trim()).filter(Boolean)

  return (
    <>
      {parts.map((part, index) => {
        const colonIndex = part.indexOf(':')
        if (colonIndex > 0) {
          const termino = part.substring(0, colonIndex).trim()
          const definicion = part.substring(colonIndex + 1).trim()
          return (
            <Box component="div" key={index} sx={{ mb: 0.5 }}>
              <strong>{termino}:</strong> {definicion}
            </Box>
          )
        }
        return (
          <Box component="div" key={index} sx={{ mb: 0.5 }}>
            {part}
          </Box>
        )
      })}
    </>
  )
}

const getCategoriaLabel = (categoria) => {
  const categorias = {
    'ley': 'Ley',
    'decreto': 'Decreto',
    'resolucion': 'Resolución',
    'circular': 'Circular',
    'directiva': 'Directiva',
    'norma_tecnica': 'Norma Técnica',
    'lineamiento': 'Lineamiento',
    'politica': 'Política'
  }
  return categorias[categoria] || categoria
}

const formatNormatividad = (normatividadTexto, normatividades, onViewNormatividad) => {
  if (!normatividadTexto) return null
  const parts = normatividadTexto.split(/[,;]+/).map(part => part.trim()).filter(part => part.length > 0)

  return (
    <>
      {parts.map((part, index) => {
        const normFound = normatividades.find(norm =>
          norm.titulo && (
            part.toLowerCase().includes(norm.titulo.toLowerCase()) ||
            norm.titulo.toLowerCase().includes(part.toLowerCase())
          )
        )
        if (normFound && normFound.archivo_url) {
          return (
            <span key={index}>
              <Button
                variant="text"
                size="small"
                onClick={() => onViewNormatividad(normFound)}
                sx={{
                  textTransform: 'none',
                  p: 0,
                  minWidth: 'auto',
                  textDecoration: 'underline',
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'transparent'
                  }
                }}
              >
                {part}
              </Button>
              {index < parts.length - 1 && ', '}
            </span>
          )
        }
        return (
          <span key={index}>
            {part}
            {index < parts.length - 1 && ', '}
          </span>
        )
      })}
    </>
  )
}

const PublicProcedimientoDetail = () => {
  const navigate = useNavigate()
  const { procedimientoId } = useParams()
  const [procedimiento, setProcedimiento] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [normatividades, setNormatividades] = useState([])
  const [openPdfModal, setOpenPdfModal] = useState(false)
  const [selectedPdfDoc, setSelectedPdfDoc] = useState(null)

  useEffect(() => {
    loadProcedimiento()
  }, [procedimientoId])

  useEffect(() => {
    loadNormatividades()
  }, [])

  const loadNormatividades = async () => {
    try {
      const response = await axios.get('/api/publico/normatividad')
      setNormatividades(response.data || [])
    } catch (err) {
      console.error('Error al cargar normatividades:', err)
    }
  }

  const handleViewNormatividad = (normatividad) => {
    setSelectedPdfDoc(normatividad)
    setOpenPdfModal(true)
  }

  const loadProcedimiento = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await axios.get(`/api/publico/procedimientos/${procedimientoId}`)
      setProcedimiento(response.data)
    } catch (err) {
      console.error('Error cargando procedimiento público:', err)
      setError('No se pudo cargar el procedimiento solicitado.')
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadPDF = () => {
    window.open(`/api/publico/procedimientos/${procedimientoId}/pdf`, '_blank')
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/publico')}>
          Volver
        </Button>
      </Container>
    )
  }

  if (!procedimiento) {
    return null
  }

  const responsables = (procedimiento.responsables || []).map((r) => r.nombre).join(', ')

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined">
          Volver
        </Button>
        <Button startIcon={<PictureAsPdf />} onClick={handleDownloadPDF} variant="contained" color="secondary">
          Descargar PDF
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        {/* Encabezado */}
        <Box sx={{ mb: 3, pb: 2, borderBottom: '2px solid #eee' }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Chip label={procedimiento.codigo} color="primary" variant="outlined" />
            <Chip label={`Versión ${procedimiento.version}`} size="small" variant="outlined" />
            {procedimiento.gestion_nombre && (
              <Chip
                label={procedimiento.gestion_nombre}
                size="small"
                sx={{ bgcolor: procedimiento.gestion_color || '#64B5F6', color: '#fff' }}
              />
            )}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {procedimiento.titulo}
          </Typography>
          {procedimiento.proceso && (
            <Typography variant="subtitle1" color="text.secondary">
              Proceso: {procedimiento.proceso}
            </Typography>
          )}
        </Box>

        {/* Objetivo */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            1. OBJETIVO
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'justify' }}>
            {procedimiento.objetivo}
          </Typography>
        </Box>

        {/* Alcance */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            2. ALCANCE
          </Typography>
          <Typography variant="body1" sx={{ textAlign: 'justify' }}>
            {procedimiento.alcance}
          </Typography>
        </Box>

        {/* Responsables */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            3. RESPONSABLES
          </Typography>
          <Typography variant="body1">
            {responsables || 'No asignado'}
          </Typography>
        </Box>

        {/* Definiciones */}
        {procedimiento.definiciones && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              4. DEFINICIONES
            </Typography>
            <Typography variant="body1" component="div" sx={{ textAlign: 'justify' }}>
              {formatDefiniciones(procedimiento.definiciones)}
            </Typography>
          </Box>
        )}

        {/* Normatividad */}
        {procedimiento.normatividad && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              5. NORMATIVIDAD
            </Typography>
            <Typography variant="body1" component="div" sx={{ textAlign: 'justify' }}>
              {formatNormatividad(procedimiento.normatividad, normatividades, handleViewNormatividad)}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Actividades */}
        {procedimiento.actividades && procedimiento.actividades.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              6. DESCRIPCIÓN DEL PROCEDIMIENTO
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, width: 40 }}>N°</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>DESCRIPCIÓN</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>INFORMACIÓN DE SOPORTE</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>INFORMACIÓN GENERADA</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {procedimiento.actividades.map((actividad) => (
                    <TableRow key={actividad.id}>
                      <TableCell sx={{ textAlign: 'center', fontWeight: 700 }}>
                        {actividad.numero ?? actividad.orden}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>
                        {actividad.titulo ? <strong>{actividad.titulo}: </strong> : null}
                        {actividad.descripcion}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{actividad.informacion_soporte || ''}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{actividad.informacion_generada || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {/* Diagrama */}
        {procedimiento.diagrama && procedimiento.diagrama.mermaid_code && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              DIAGRAMA DE FLUJO
            </Typography>
            <DiagramaRender mermaidCode={procedimiento.diagrama.mermaid_code} />
          </Box>
        )}
      </Paper>

      {/* Modal de vista previa del PDF de normatividad */}
      <Dialog
        open={openPdfModal}
        onClose={() => setOpenPdfModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PictureAsPdf color="error" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6">
                {selectedPdfDoc?.titulo}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedPdfDoc?.categoria && getCategoriaLabel(selectedPdfDoc.categoria)}
                {selectedPdfDoc?.numero && ` • ${selectedPdfDoc.numero}`}
                {selectedPdfDoc?.fecha_expedicion && ` • ${selectedPdfDoc.fecha_expedicion}`}
              </Typography>
            </Box>
            <Button
              startIcon={<Download />}
              onClick={() => {
                if (selectedPdfDoc?.archivo_url) {
                  const link = document.createElement('a')
                  link.href = selectedPdfDoc.archivo_url
                  link.download = selectedPdfDoc.archivo_nombre || selectedPdfDoc.titulo
                  link.click()
                }
              }}
              size="small"
            >
              Descargar
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {selectedPdfDoc?.archivo_url ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flexGrow: 1, position: 'relative' }}>
                <iframe
                  src={`${selectedPdfDoc.archivo_url}#toolbar=1&navpanes=1&scrollbar=1`}
                  width="100%"
                  height="100%"
                  style={{ border: 'none', minHeight: '500px' }}
                  title={`Vista previa: ${selectedPdfDoc.titulo}`}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '400px',
              flexDirection: 'column',
              gap: 2
            }}>
              <PictureAsPdf sx={{ fontSize: 64, color: 'grey.400' }} />
              <Typography color="text.secondary">
                No hay documento disponible para mostrar
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPdfModal(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

export default PublicProcedimientoDetail
